<template>
  <div class="bg-gray-900 min-h-screen w-full">
    <div class="px-4 py-8 mx-auto container">
      <!-- Back button -->
      <button
        @click="$router.go(-1)"
        class="flex items-center text-gray-400 hover:text-blue-400 mb-6 transition-colors"
      >
        <span class="mr-2">&larr;</span> Nazad na oglas
      </button>
      <!-- Loader -->
      <div v-if="showMainLoader" class="flex justify-center items-center py-20">
        <div
          class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"
        ></div>
      </div>

      <!-- Error message -->
      <div v-else-if="error" class="bg-red-900 text-white p-4 rounded-lg mb-6">
        {{ error }}
      </div>

      <!-- Profile content -->
      <template v-else-if="user">
        <!-- Header with user info and follow button -->
        <ProfileHeader
          :user="user"
          :following="following"
          :followers-count="followersCount"
          :is-loading="isLoading"
          @toggle-follow="handleToggleFollow"
        />

        <!-- Tab navigation -->
        <ProfileTabs :active-tab="activeTab" @change-tab="handleTabChange" />

        <!-- Products section -->
        <ProfileProducts
          v-if="activeTab === 'products'"
          :user-id="user.id"
          :username="user.username"
          :user-city="user.city"
        />

        <!-- Ratings section -->
        <div v-else-if="activeTab === 'ratings'">
          <div
            v-if="showRatingsLoader"
            class="flex justify-center items-center py-10"
          >
            <div
              class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"
            ></div>
          </div>
          <div v-else>
            <!-- Fallback rating stats when actual data hasn't loaded yet -->
            <ProfileRatings
              :username="user.username"
              :ratings="filteredRatings"
              :stats="ratingStats || defaultRatingStats"
              :loading="false"
              :current-page="ratingCurrentPage"
              :meta="ratingsMeta || defaultMeta"
              :user-role="ratingUserRole"
              :rating-filter="ratingFilter"
              :auth-required="authRequired"
              @change-role="changeRatingRole"
              @change-page="changeRatingPage"
              @change-filter="changeRatingFilter"
            />
          </div>
        </div>
      </template>
    </div>

    <!-- Toast Notification -->
    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      @close="toast.show = false"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  watch,
  computed,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import ToastNotification from '@/components/ToastNotification.vue';
import { useUserFollow } from '@/composables/useUserFollow';
import { useUserRatings } from '@/composables/useUserRatings';
import api from '@/services/api';
import { debounce } from '@/utils/debounce';

// Import refactored components
import ProfileHeader from '@/components/profile/ProfileHeader.vue';
import ProfileTabs from '@/components/profile/ProfileTabs.vue';
import ProfileProducts from '@/components/profile/ProfileProducts.vue';
import ProfileRatings from '@/components/profile/ProfileRatings.vue';

interface User {
  id: string;
  username: string;
  city: string;
  positiveRatings: number;
  negativeRatings: number;
  followersCount: number;
}

interface ProfileResponse {
  status: string;
  message: string;
  data: {
    user: User;
    products: any[];
    meta: any;
  };
}

// Default data for when ratings haven't loaded yet
const defaultRatingStats = {
  seller: { likes: 0, dislikes: 0 },
  buyer: { likes: 0, dislikes: 0 },
};

const defaultMeta = {
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 1,
};

type RatingFilter = 'all' | 'positive' | 'negative';

export default defineComponent({
  name: 'ProfileView',
  components: {
    ToastNotification,
    ProfileHeader,
    ProfileTabs,
    ProfileProducts,
    ProfileRatings,
  },
  setup() {
    // Main user state
    const user = ref<User | null>(null);
    const userRef = ref<User | null>(null);
    const userId = ref<string | null>(null);
    const loading = ref(true);
    const showMainLoader = ref(false);
    const error = ref<string | null>(null);
    const activeTab = ref('products');
    const ratingsLoaded = ref(false);
    const ratingFilter = ref<RatingFilter>('all');
    const showRatingsLoader = ref(false);

    // Debounced main loader functions
    const debouncedShowMainLoader = debounce(() => {
      showMainLoader.value = true;
    }, 500);

    const hideMainLoaderImmediately = () => {
      debouncedShowMainLoader.cancel();
      showMainLoader.value = false;
    };

    // Watch main loading state
    watch(loading, (isLoading) => {
      if (isLoading) {
        debouncedShowMainLoader();
      } else {
        hideMainLoaderImmediately();
      }
    });

    // Debounced ratings loader functions
    const debouncedShowRatingsLoader = debounce(() => {
      showRatingsLoader.value = true;
    }, 300);

    const hideRatingsLoaderImmediately = () => {
      debouncedShowRatingsLoader.cancel();
      showRatingsLoader.value = false;
    };

    // User follow functionality
    const { following, followersCount, toast, isLoading, handleToggleFollow } =
      useUserFollow(userRef);

    // User ratings functionality
    const {
      ratings,
      stats: ratingStats,
      loading: ratingsLoading,
      meta: ratingsMeta,
      currentPage: ratingCurrentPage,
      userRole: ratingUserRole,
      authRequired, // ← Dodano
      fetchRatings,
      fetchRatingStats,
      changePage: changeRatingPage,
      changeUserRole: changeRatingRole,
    } = useUserRatings(userId);

    // Watch ratings loading state to control spinner
    watch(ratingsLoading, (isLoading) => {
      if (isLoading) {
        debouncedShowRatingsLoader();
      } else {
        hideRatingsLoaderImmediately();
      }
    });

    // Filtered ratings based on selected filter
    const filteredRatings = computed(() => {
      if (!ratings.value) return [];

      if (ratingFilter.value === 'all') {
        return ratings.value;
      } else if (ratingFilter.value === 'positive') {
        return ratings.value.filter((rating) => rating.isLike);
      } else {
        return ratings.value.filter((rating) => !rating.isLike);
      }
    });

    // Change rating filter
    const changeRatingFilter = (filter: RatingFilter) => {
      ratingFilter.value = filter;
    };

    // Handle tab change
    const handleTabChange = (newTab: string) => {
      activeTab.value = newTab;

      if (newTab === 'ratings' && user.value && !ratingsLoaded.value) {
        loadRatingsData();
      }
    };

    // Load ratings data
    const loadRatingsData = async () => {
      if (!userId.value) return;

      try {
        // Load both ratings and stats
        await Promise.all([
          fetchRatings(1, ratingUserRole.value),
          fetchRatingStats(),
        ]);

        ratingsLoaded.value = true;
      } catch (error) {
        console.error('Error loading ratings data:', error);
      }
    };

    // Watch for route changes
    watch(
      () => window.location.href,
      () => {
        fetchUserProfile();
      }
    );

    // Watch for user data and update the userRef for useUserFollow composable
    watch(user, (newUser) => {
      if (newUser) {
        userRef.value = newUser;
        userId.value = newUser.id;

        // Reset ratings loaded flag when user changes
        ratingsLoaded.value = false;

        // If already on ratings tab, load the data immediately
        if (activeTab.value === 'ratings') {
          loadRatingsData();
        }
      }
    });

    // Watch for followersCount changes to update user object
    watch(followersCount, (newCount) => {
      if (user.value) {
        user.value.followersCount = newCount;
      }
    });

    const fetchUserProfile = async () => {
      loading.value = true;
      error.value = null;

      const userId = new URL(window.location.href).pathname.split('/').pop();

      if (!userId) {
        error.value = 'Neispravan ID korisnika.';
        loading.value = false;
        return;
      }

      try {
        const response = await api.get<ProfileResponse>(
          `/api/products/user/${userId}/detailed`
        );

        if (response.data.status === 'success') {
          user.value = response.data.data.user;
        } else {
          error.value = 'Greška prilikom učitavanja korisničkog profila.';
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        error.value = 'Greška prilikom komunikacije sa serverom.';
      } finally {
        loading.value = false;
      }
    };

    // Initial fetch on setup
    onMounted(() => {
      fetchUserProfile();
    });

    // Clean up on unmount
    onBeforeUnmount(() => {
      debouncedShowMainLoader.cancel();
      debouncedShowRatingsLoader.cancel();
    });

    return {
      user,
      loading,
      showMainLoader,
      error,
      activeTab,
      following,
      followersCount,
      isLoading,
      handleToggleFollow,
      toast,
      ratings,
      ratingStats,
      ratingsLoading,
      ratingsMeta,
      ratingCurrentPage,
      ratingUserRole,
      authRequired, // ← Dodano u return
      changeRatingPage,
      changeRatingRole,
      handleTabChange,
      defaultRatingStats,
      defaultMeta,
      ratingFilter,
      filteredRatings,
      changeRatingFilter,
      showRatingsLoader,
    };
  },
});
</script>
